import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

// Images
import img1 from '@/assets/decorative/benefits/01-baixar-app.jpg';
import img2 from '@/assets/decorative/benefits/02-criar-conta.jpg';
import img3 from '@/assets/decorative/benefits/03-descobrir-cafes.jpg';
import img4 from '@/assets/decorative/benefits/04-cafeterias-proximas.jpg';
import img5 from '@/assets/decorative/benefits/05-saboreia-cafes.jpg';
import img6 from '@/assets/decorative/benefits/06-fidelidade-ativa.png';
import img7 from '@/assets/decorative/benefits/07-cafe-de-volta.jpg';
import img8 from '@/assets/decorative/benefits/08-ranking-cafe.jpg';

export function BenefitsMobileCarousel() {
    const { t } = useTranslation();
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [Autoplay({ delay: 4000, stopOnInteraction: true })]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    const cards = [
        { img: img1, key: 0 },
        { img: img2, key: 1 },
        { img: img3, key: 2 },
        { img: img4, key: 3 },
        { img: img5, key: 4 },
        { img: img6, key: 5 },
        { img: img7, key: 6 },
        { img: img8, key: 7 },
    ];

    const scrollTo = useCallback((index: number) => {
        emblaApi && emblaApi.scrollTo(index);
    }, [emblaApi]);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        setScrollSnaps(emblaApi.scrollSnapList());
        emblaApi.on('select', onSelect);
        return () => {
            emblaApi.off('select', onSelect);
        };
    }, [emblaApi, onSelect]);

    return (
        <div className="xl:hidden w-full relative">
            {/* Carousel viewport */}
            <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex touch-pan-y -ml-4">
                    {cards.map((card, index) => (
                        <div className="flex-[0_0_85%] min-w-0 pl-4 relative" key={index}>
                            <motion.div
                                className={cn(
                                    "flex flex-col items-center bg-[#FDF8F3] rounded-[32px] overflow-hidden shadow-lg border border-[#E5DCD3] h-full transition-opacity duration-300",
                                    selectedIndex === index ? "opacity-100 scale-100" : "opacity-70 scale-95"
                                )}
                            >
                                <div className="w-full aspect-square relative overflow-hidden">
                                    <img
                                        src={card.img}
                                        alt={t(`consumer.benefits.items.${card.key}.title`)}
                                        className="w-full h-full object-cover"
                                    />
                                    {/* Subtle gradient overlay for text readability if needed, though design has text below */}
                                </div>

                                <div className="p-6 text-center">
                                    <h3 className="font-display font-bold text-xl text-[#2D1810] mb-2 leading-tight">
                                        {t(`consumer.benefits.items.${card.key}.title`)}
                                    </h3>
                                    <p className="text-[#6B5B4F] text-sm leading-relaxed px-2">
                                        {t(`consumer.benefits.items.${card.key}.desc`)}
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Pagination Dots */}
            <div className="flex justify-center gap-2 mt-6">
                {scrollSnaps.map((_, index) => (
                    <button
                        key={index}
                        className={cn(
                            "w-2 h-2 rounded-full transition-all duration-300",
                            index === selectedIndex ? "bg-[#6F4E37] w-6" : "bg-[#6F4E37]/30"
                        )}
                        onClick={() => scrollTo(index)}
                        aria-label={`Go to slide ${index + 1}`}
                    />
                ))}
            </div>
        </div>
    );
}
